/**
 * Square "social-media" image tile.
 *
 * Shows the FULL image (never cropped) centered on a square canvas, and fills
 * the leftover space with a blurred, zoomed copy of the same image — the
 * Instagram-feed treatment. Gives a uniform square grid without cropping
 * off heads/ties on off-square covers, and without needing to re-crop the
 * source assets.
 *
 * The blurred backdrop is a tiny (96px-wide) rendition scaled up under a CSS
 * blur, so it costs a few KB; the sharp foreground reuses the same asset at a
 * card-appropriate width. Both go through the Sanity CDN via `?w=…` params.
 *
 * No hooks → usable from server or client components.
 */

function appendParams(url: string, params: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}${params}`
}

export function SquareBlurImage({
  src,
  alt,
  className = '',
  imgClassName = '',
  /** Foreground width requested from the CDN. Bump for larger tiles. */
  width = 600,
}: {
  src: string
  alt: string
  /** Extra classes on the square container. */
  className?: string
  /** Extra classes on the sharp foreground image (e.g. a hover transform). */
  imgClassName?: string
  width?: number
}) {
  const bg = appendParams(src, 'w=96&auto=format')
  const fg = appendParams(src, `w=${width}&auto=format`)
  return (
    <div className={`relative aspect-square overflow-hidden bg-gray-100 ${className}`}>
      {/* Blurred fill. scale-110 hides the blur bleeding past the edges. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={bg}
        alt=""
        aria-hidden
        loading="lazy"
        className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl"
      />
      {/* Full image, contained (nothing cropped). */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={fg}
        alt={alt}
        loading="lazy"
        className={`relative z-10 h-full w-full object-contain ${imgClassName}`}
      />
    </div>
  )
}
